import { z } from 'zod';

import { Join, JoinCondition } from '@models/feature-descriptor';
import { Column, TableColumns, TableName } from '@models/column';

import {
  ColumnDraft,
  NumberDraft,
  OnlyColumnFields,
} from './common-field-draft';

type JoinConditionColumnFields = OnlyColumnFields<JoinCondition>;

export type JoinDraft = Partial<{
  target_table: TableName;
  source_table: TableName;
  join_conditions: JoinConditionDraft[];
}>;

export type JoinConditionDraft = Partial<JoinCondition> & { id: number };

const GeoJoinConditionDraft = z.object({
  type: z.literal(JoinCondition.Type.Geo),
});

const SameJoinConditionDraft = z.object({
  type: z.literal(JoinCondition.Type.Same),
  target_side_column: ColumnDraft,
  source_side_column: ColumnDraft,
});

const PeriodicTimeJoinConditionDraft = z.object({
  type: z.literal(JoinCondition.Type.PeriodicTime),
  target_side_column: ColumnDraft,
  source_side_column: ColumnDraft,
  periodic_unit: z.string(),
  num_cycles: NumberDraft,
});

const TimeJoinConditionDraft = z.object({
  type: z.literal(JoinCondition.Type.Time),
  target_side_column: ColumnDraft,
  source_side_column: ColumnDraft,
  from_value: z.string(),
  to_value: z.string(),
});

const JoinConditionsDrafts: Record<JoinCondition.Type, z.ZodObject<any>> = {
  [JoinCondition.Type.Geo]: GeoJoinConditionDraft,
  [JoinCondition.Type.Same]: SameJoinConditionDraft,
  [JoinCondition.Type.PeriodicTime]: PeriodicTimeJoinConditionDraft,
  [JoinCondition.Type.Time]: TimeJoinConditionDraft,
};

export namespace JoinDraft {
  export function fromJoin(join: Join, tableColumns: TableColumns) {
    return {
      target_table: join.target_table,
      source_table: join.source_table,
      join_conditions: join.join_conditions.map((condition: JoinCondition) => {
        const schema = JoinConditionsDrafts[condition.type];

        const columnFields = Object.entries(schema.shape)
          .filter(([_, field]: [any, any]) => field.description === 'Column')
          .map(([colName]) => colName) as Array<
          keyof JoinConditionColumnFields
        >;

        return {
          id: Math.random(),
          ...condition,
          ...Object.fromEntries(
            columnFields.map(field => [
              field,
              Column.fromFullName(condition[field], tableColumns) ?? {},
            ])
          ),
        };
      }),
    };
  }

  export function validate(joinDraft: JoinDraft) {
    return z
      .object({
        target_table: z.string(),
        source_table: z.string(),
        join_conditions: z
          .union([
            GeoJoinConditionDraft,
            SameJoinConditionDraft,
            TimeJoinConditionDraft,
            PeriodicTimeJoinConditionDraft,
          ])
          .array()
          .nonempty(),
      })
      .safeParse(joinDraft);
  }
}
